import { URL } from './URL'

/** Custom scheme registered in app.json + AndroidManifest intent-filters. */
export const APP_SCHEME = 'pokerpower'

/**
 * Every deep link is normalized onto this host before it is stored, so
 * `InitialLinkingStore` (which does `new URL(value)`) always gets an absolute
 * URL whose `pathname` / `search` / `searchParams` are the router target.
 */
export const CANONICAL_HOST = 'pokerpower.com'

/**
 * https deep links we accept. Mirrors `ios.associatedDomains` in app.json and the
 * Android `intentFilters` in app.config.js, plus the Branch link domains. Anything
 * else is dropped rather than navigated to — a push payload is untrusted input.
 */
const TRUSTED_HOSTS = new Set([
  'pokerpower.com',
  'www.pokerpower.com',
  'app.pokerpower.com',
  'app.dev.pokerpower.com',
  'app.sandbox.pokerpower.com',
  'games.ppwr.io',
  'games-dev.ppwr.io',
  'gjva8.app.link',
  'gjva8-alternate.app.link',
  'gjva8.test-app.link',
  'gjva8-alternate.test-app.link'
])

/** Query keys that mark an Auth0/AuthSession callback rather than a navigable link. */
const AUTH_CALLBACK_PARAMS = ['code', 'state', 'error']

export type DeepLinkTarget = {
  /** expo-router href — path plus query string, e.g. `/invite/abc?redirectUri=/x`. */
  href: string
  /** Absolute canonical URL, safe to hand to `new URL()`. */
  url: string
}

export type DeepLinkSkipReason =
  | 'empty'
  | 'invalid_url'
  | 'untrusted_host'
  | 'unsupported_scheme'
  | 'auth_callback'
  | 'no_path'

export type DeepLinkResolution = { ok: true; target: DeepLinkTarget } | { ok: false; reason: DeepLinkSkipReason }

function buildTarget(pathWithQuery: string): DeepLinkTarget {
  const href = pathWithQuery.startsWith('/') ? pathWithQuery : `/${pathWithQuery}`
  return { href, url: `https://${CANONICAL_HOST}${href}` }
}

function isAuthCallback(search: string) {
  if (!search) return false
  const query = search.startsWith('?') ? search.slice(1) : search
  // Plain string match — RN's global URLSearchParams is not dependable across platforms.
  return query.split('&').some((pair) => AUTH_CALLBACK_PARAMS.includes(pair.split('=')[0].toLowerCase()))
}

function fromPathAndSearch(path: string, search: string): DeepLinkResolution {
  const normalized = path && path !== '/' ? path : ''
  // A bare `pokerpower://?code=…` is the AuthSession redirect coming back, not a link.
  if (!normalized) {
    return { ok: false, reason: isAuthCallback(search) ? 'auth_callback' : 'no_path' }
  }
  return { ok: true, target: buildTarget(`${normalized}${search}`) }
}

/**
 * Normalize a deep link from any push provider into a router target.
 *
 * Accepts all the shapes our campaigns actually send:
 * - OneSignal `additionalData.navigate`: a bare router path (`/invite/abc`)
 * - Customer.io link / OneSignal `launchURL`: `pokerpower://invite/abc`
 * - Universal / Branch link: `https://pokerpower.com/invite/abc?x=1`
 *
 * Custom-scheme URLs need care: WHATWG parses `pokerpower://invite/abc` as
 * host=`invite`, pathname=`/abc`, so reading `pathname` alone silently drops the
 * first segment. We rejoin host + pathname to recover the real route.
 */
export function resolveDeepLink(raw?: string | null): DeepLinkResolution {
  const value = raw?.trim()
  if (!value) return { ok: false, reason: 'empty' }

  // OneSignal campaigns send an expo-router path, which is not a parseable URL.
  if (value.startsWith('/')) {
    const queryIndex = value.indexOf('?')
    const path = queryIndex === -1 ? value : value.slice(0, queryIndex)
    const search = queryIndex === -1 ? '' : value.slice(queryIndex)
    return fromPathAndSearch(path, search)
  }

  // `URL` is a value export (polyfill on native, window.URL on web), so derive the type.
  let parsed: InstanceType<typeof URL>
  try {
    parsed = new URL(value)
  } catch {
    return { ok: false, reason: 'invalid_url' }
  }

  const scheme = parsed.protocol.replace(':', '').toLowerCase()

  if (scheme === APP_SCHEME) {
    const host = parsed.host ? `/${parsed.host}` : ''
    return fromPathAndSearch(`${host}${parsed.pathname}`, parsed.search)
  }

  if (scheme === 'http' || scheme === 'https') {
    if (!TRUSTED_HOSTS.has(parsed.host.toLowerCase())) {
      return { ok: false, reason: 'untrusted_host' }
    }
    return fromPathAndSearch(parsed.pathname, parsed.search)
  }

  return { ok: false, reason: 'unsupported_scheme' }
}

/**
 * Pull the deep link out of a OneSignal notification, tolerating both the
 * OneSignal format (`additionalData.navigate`) and the Customer.io format
 * (`link` / `url`), so a campaign authored for either provider still routes.
 */
export function extractOneSignalDeepLink(notification: {
  additionalData?: unknown
  launchURL?: string | null
}): string | undefined {
  const data = (notification.additionalData ?? {}) as Record<string, unknown>
  const candidates = [data.navigate, data.link, data.url, data.deeplink, notification.launchURL]
  return candidates.find((value): value is string => typeof value === 'string' && value.trim() !== '')
}
